// Coordinate math for the drag-and-drop form builder.
//
// The stored formMarkup coordinates (`left`/`top`) are A4 millimetres, origin
// top-left, exactly as consumed by `overlayInfo` (docOvelayTool.ts), which draws
// each page image stretched to a full A4 jsPDF page and calls `doc.text(text,
// left, top)`. Because the page image is stretched to fill the whole A4 page
// (aspect ratio discarded), the on-screen canvas can use a pure linear scale.
//
// We render the canvas at A4 @ 72dpi so 1pt === 1px and 12pt text === 12px —
// making the live text preview visually match the generated PDF.

// jsPDF's default document is portrait / mm / A4. A4 is a fixed standard.
export const PAGE_W_MM = 210;
export const PAGE_H_MM = 297;

// A4 at 72dpi (72 / 25.4 = 2.8346 px per mm). 210mm -> 595px, 297mm -> 842px.
export const BASE_W = 595;
export const BASE_H = Math.round((BASE_W * PAGE_H_MM) / PAGE_W_MM); // 842

// overlayInfo sets font size 12pt (THRESHOLD_FONTSIZE) and jsPDF's doc.text
// anchors on the BASELINE. A chip's visual top is the glyph top, so text chips
// are lifted by the font ascent to make the on-canvas baseline match the PDF.
// 1pt = 0.352778mm; ascent is ~0.8 of the em for the default Helvetica.
export const FONT_PT = 12;
export const PT_TO_MM = 0.352778;
export const BASELINE_ASCENT_MM = FONT_PT * PT_TO_MM * 0.8; // ~3.39mm

// Font height in base-canvas px (12pt at 72dpi === 12px).
export const PX_PER_PT = (PT_TO_MM * BASE_H) / PAGE_H_MM; // ~1.0 at the 72dpi base
export const FONT_PX = FONT_PT * PX_PER_PT;

// Line-height factor used to size a text chip's box from its font size.
export const LINE_HEIGHT = 1.35;

export const DEFAULT_FONT_PT = FONT_PT; // 12
export const MIN_FONT_PT = 6;
export const MAX_FONT_PT = 96;

export const ptToPx = (pt: number) => pt * PX_PER_PT;
export const pxToPt = (px: number) => px / PX_PER_PT;

// Signature is stamped by overlayInfo as a fixed 40mm x 10mm image, anchored at
// its top-left (addImage), NOT on a text baseline.
export const SIGNATURE_W_MM = 40;
export const SIGNATURE_H_MM = 10;

export const mmToPx = (leftMm: number, topMm: number, w: number = BASE_W, h: number = BASE_H) => ({
  x: (leftMm / PAGE_W_MM) * w,
  y: (topMm / PAGE_H_MM) * h,
});

export const pxToMmX = (xPx: number, w: number = BASE_W) => (xPx / w) * PAGE_W_MM;
export const pxToMmY = (yPx: number, h: number = BASE_H) => (yPx / h) * PAGE_H_MM;

// Round to one decimal so stored strings stay tidy (matches the granularity a
// user could reasonably type).
export const roundMm = (v: number): number => Math.round(v * 10) / 10;

export const clampMm = (v: number, maxMm: number): number => Math.min(Math.max(v, 0), maxMm);
